import { preferTrainTrips } from "../../../Lib/Utils/booking";
import { isNolAdmission } from "../../../Lib/Utils/nolCard";

/**
 * Who a bookings-list row names, and who holds what — the passenger rules the
 * list mapper (GetBookingToRightDataAction) applies when it splits a booking
 * into cards after a partial change-date. Pure and kept here, rather than as
 * closures inside that 3000-line mapper, so each rule is tested on its own.
 */

/** Admission statuses that mean the rider no longer travels on it. */
const NOT_TRAVELLING_STATUSES: ReadonlySet<string> = new Set(["EXCHANGED", "REFUNDED", "RELEASED"]);

const admissionsOf = (offers: any[]): any[] => offers.flatMap((offer: any) => offer?.admissions || []);
const ridersOf = (admissions: any[]): string[] => admissions.flatMap((admission: any) => admission?.passengerIds || []);
const isTravelling = (admission: any): boolean => !NOT_TRAVELLING_STATUSES.has(admission?.status);

/**
 * An offer's riders, EXCHANGED admissions left out.
 *
 * An admission without `passengerIds` contributes nobody. The mapper's inline
 * copy this replaced pushed `undefined` instead, which drew a trip with an
 * unnamed rider (or keyed a group on ""); callers now skip such a trip as they
 * skip one with no riders at all. Not marked required in the retailer spec, but
 * no real payload without it is known.
 */
export const nonExchangedPassengerIds = (offer: any): string[] =>
    ridersOf((offer?.admissions || []).filter((admission: any) => admission?.status !== "EXCHANGED"));

/**
 * Who still travels on a trip — the OVERLAP test that groups one-way trips into
 * cards. Not the card's passenger list, which keeps its cancelled riders (a
 * partial cancel still names them).
 *
 * Cancelled riders are left out while anyone on the trip still travels: a
 * change-date refunds the moved passenger's shuttle (refundLegs) but leaves
 * their admission on the shared shuttle offer, so counting it made that shuttle
 * overlap BOTH passenger groups and fused the old and new trains onto one card.
 * A trip nobody travels on any more keeps its cancelled riders, or it could join
 * no group at all.
 */
export const groupingPassengerIdsForTrip = (tripOffers: any[]): Set<string> => {
    const live = admissionsOf(tripOffers).filter(isTravelling);
    return new Set<string>(live.length > 0 ? ridersOf(live) : tripOffers.flatMap(nonExchangedPassengerIds));
};

/**
 * A change-date moved this rider off the group's train: EXCHANGED there and
 * nothing live left in the group. What stays behind is history — their refunded
 * shuttle — and naming them on this card put them on a train they left and
 * joined the card to their new one (collapseBookingRows). A rider who CANCELLED
 * (refunded, never exchanged) is still named, as before.
 */
export const wasMovedOffGroup = (passengerId: string, offers: any[]): boolean => {
    const own = admissionsOf(offers).filter((admission: any) => (admission?.passengerIds || []).includes(passengerId));
    return own.some((admission: any) => admission?.status === "EXCHANGED") && !own.some(isTravelling);
};

/** Riders a change-date moved somewhere on this booking — mobile's `passengersWithExchangedStatus`. */
export const movedPassengerIdsOf = (bookedOffers: any[]): Set<string> =>
    new Set<string>(ridersOf(admissionsOf(bookedOffers).filter((admission: any) => admission?.status === "EXCHANGED")));

/**
 * An offer's riders for round-trip change-date grouping: its non-exchanged
 * riders, minus a MOVED rider with nothing live left on it. A change-date
 * refunds the moved passenger's feeder shuttle but leaves that REFUNDED
 * admission on the shared offer, which put them in the unchanged party's group
 * and folded both groups onto one card — losing their new train. A rider who
 * merely cancelled is still counted, as before.
 */
export const cdtGroupingIds = (offer: any, movedPassengerIds: ReadonlySet<string>): string[] => {
    const liveIds = new Set<string>(ridersOf((offer?.admissions || []).filter(isTravelling)));
    return nonExchangedPassengerIds(offer).filter((id) => liveIds.has(id) || !movedPassengerIds.has(id));
};

/**
 * Who holds a ticket on ONE leg of a next-ticket row, read off the booking — or
 * null when there is nothing to read, and the caller keeps the row's own riders.
 *
 * Not the row's passengers: since a partial change-date keeps its own card
 * (collapseBookingRows), the row is ONE passenger group, while the leg it names
 * can be shared with another group — the unchanged inbound after an outbound
 * change, or the reverse. Narrowing to the row's group dropped the moved
 * passenger from that shared leg on every surface. Per TRIP, not per direction:
 * after an outbound change both outbound trips are live, and the direction-wide
 * set put the moved passenger on the train they left.
 *
 * The leg is the row's TRAIN trip in that direction, looked up on the booking —
 * not `tripIds[0]`/`[1]`, whose order a fold can reshuffle. By `trip.direction`,
 * unlike splitRowTripsByLeg, which goes through shuttleServesDirection: that
 * rule exists for SHUTTLES, which the backend stamps OUT_BOUND whichever train
 * they serve, while a train trip's own direction is reliable — and this looks at
 * trains only. Only OFFER-level coverage counts: a Nol pass rides its own offer
 * with none, and Turnit leaves the moved passenger's pass FULFILLED on the old
 * leg, which must not put them back on that train.
 *
 * Null (fail open to the row's riders) when no FULFILLED ticket is found: an
 * ON_HOLD or mid-exchange leg, a train trip missing from the booking, or offers
 * without coverage.
 */
export const legHolderIds = (booking: any, rowTripIds: string[] | undefined, direction: string): Set<string> | null => {
    const rowTrip = preferTrainTrips<any>(booking?.trips || [])
        .filter((trip: any) => (rowTripIds || []).includes(trip.id) && trip.direction === direction)
        .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
    if (!rowTrip) return null;
    const holders = new Set<string>(
        ridersOf(
            admissionsOf((booking?.bookedOffers || []).filter((offer: any) => offer?.tripCoverage?.coveredTripId === rowTrip.id))
                .filter((admission: any) => admission?.status === "FULFILLED" && !isNolAdmission(admission))
        )
    );
    return holders.size > 0 ? holders : null;
};
